import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackLandingPage } from '../../../tracking';
import {
  FAA_LANDING_PAGE_MODULE,
  getVisibleLandingPageModules,
  trackSelectSearchSuggestion,
} from '../../../tracking/landing-page';
import type { FaaLandingPageModule } from '../../../tracking/landing-page';
import type { Option } from '../../search-autocomplete/types';

interface UseLandingPageTrackingOptions {
  mixpanelHasInitialized: boolean;
  showCallToActions: boolean;
  showUltimateGuide: boolean;
  showSuburbSnapshot: boolean;
}

interface UseLandingPageTrackingResult {
  modules: FaaLandingPageModule[];
  onModuleRendered: (module: FaaLandingPageModule) => void;
  onSuggestionSelect: (params: {
    item: Option;
    position: number;
    textTyped: string;
    totalSuggestions?: number;
  }) => void;
  onSearchInputInteraction: () => void;
  onTapSearch: (options: Option[]) => void;
}

const MAX_PENDING_TAP_SEARCHES = 10;

const useLandingPageTracking = ({
  mixpanelHasInitialized,
  showCallToActions,
  showUltimateGuide,
  showSuburbSnapshot,
}: UseLandingPageTrackingOptions): UseLandingPageTrackingResult => {
  const hasTrackedView = useRef(false);
  const hasTrackedSearchInputInteraction = useRef(false);
  const hasPendingSearchInputInteraction = useRef(false);
  const pendingTapSearches = useRef<
    { options: Option[]; modules: FaaLandingPageModule[] }[]
  >([]);
  const pendingSuggestionSelect = useRef<
    Parameters<typeof trackSelectSearchSuggestion>[0] | null
  >(null);
  const [loadedModules, setLoadedModules] = useState<FaaLandingPageModule[]>(
    [],
  );

  const visibleModules = useMemo(
    () =>
      getVisibleLandingPageModules({
        hasContentCarouselGuides: showUltimateGuide,
        hasCtaDriverGetAppraisal: showCallToActions,
        hasCtaDriverTrackHome: showCallToActions,
        hasCtaDriverRecentlySold: showCallToActions,
        hasSuburbSnapshot: showSuburbSnapshot,
      }),
    [showUltimateGuide, showCallToActions, showSuburbSnapshot],
  );

  const onModuleRendered = useCallback((module: FaaLandingPageModule): void => {
    setLoadedModules((previousModules) => {
      if (previousModules.includes(module)) {
        return previousModules;
      }

      return [...previousModules, module];
    });
  }, []);

  useEffect(() => {
    if (showSuburbSnapshot) {
      onModuleRendered(FAA_LANDING_PAGE_MODULE.SUBURB_SNAPSHOT);
    }
  }, [showSuburbSnapshot, onModuleRendered]);

  useEffect(() => {
    const allVisibleModulesLoaded = visibleModules.every((module) =>
      loadedModules.includes(module),
    );

    if (
      mixpanelHasInitialized &&
      allVisibleModulesLoaded &&
      !hasTrackedView.current
    ) {
      hasTrackedView.current = true;
      trackLandingPage.trackViewLandingPage(visibleModules);
    }
  }, [mixpanelHasInitialized, visibleModules, loadedModules]);

  const onSearchInputInteraction = useCallback((): void => {
    if (hasTrackedSearchInputInteraction.current) {
      return;
    }

    if (mixpanelHasInitialized) {
      hasTrackedSearchInputInteraction.current = true;
      trackLandingPage.trackSearchInputInteraction();
    } else {
      hasPendingSearchInputInteraction.current = true;
    }
  }, [mixpanelHasInitialized]);

  useEffect(() => {
    if (
      mixpanelHasInitialized &&
      hasPendingSearchInputInteraction.current &&
      !hasTrackedSearchInputInteraction.current
    ) {
      hasPendingSearchInputInteraction.current = false;
      hasTrackedSearchInputInteraction.current = true;
      trackLandingPage.trackSearchInputInteraction();
    }
  }, [mixpanelHasInitialized]);

  const onTapSearch = useCallback(
    (options: Option[]): void => {
      trackLandingPage.trackTapSearchGa4(options);

      if (mixpanelHasInitialized) {
        trackLandingPage.trackTapSearchMixpanel(options, visibleModules);
      } else if (pendingTapSearches.current.length < MAX_PENDING_TAP_SEARCHES) {
        pendingTapSearches.current.push({ options, modules: visibleModules });
      }
    },
    [mixpanelHasInitialized, visibleModules],
  );

  const onSuggestionSelect = useCallback<
    UseLandingPageTrackingResult['onSuggestionSelect']
  >(
    (params) => {
      const paramsWithModules = { ...params, modules: visibleModules };

      if (mixpanelHasInitialized) {
        trackSelectSearchSuggestion(paramsWithModules);
      } else {
        pendingSuggestionSelect.current = paramsWithModules;
      }
    },
    [mixpanelHasInitialized, visibleModules],
  );

  useEffect(() => {
    if (mixpanelHasInitialized && pendingTapSearches.current.length > 0) {
      const pending = pendingTapSearches.current;
      pendingTapSearches.current = [];
      pending.forEach(({ options, modules }) =>
        trackLandingPage.trackTapSearchMixpanel(options, modules),
      );
    }
  }, [mixpanelHasInitialized]);

  useEffect(() => {
    if (mixpanelHasInitialized && pendingSuggestionSelect.current) {
      trackSelectSearchSuggestion(pendingSuggestionSelect.current);
      pendingSuggestionSelect.current = null;
    }
  }, [mixpanelHasInitialized]);

  return {
    modules: visibleModules,
    onModuleRendered,
    onSuggestionSelect,
    onSearchInputInteraction,
    onTapSearch,
  };
};

export default useLandingPageTracking;
